import React, { useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { LoadingButton } from '@lf/ui-primitives';
import StepIndicator from '../../components/StepIndicator';
import { FunLoader, LoadingIndicator } from '@lf/ui-primitives';
import ReviewLessons from './ReviewLessons';
import ReviewTopics from './ReviewTopics';
import { streamStudyPlan, streamStudyPlanTopics, streamPreviousKnowledge, streamGoals, getEmojis } from '@lf/api-client/lfApiStudyPlan';

function getStudyPlan(plan, lessons) {
    return {
        subject: plan.subject,
        goal: plan.goal,
        previous_knowledge: plan.previous_knowledge,
        needs: plan.needs,
        emoji: plan.emoji,
        num_lessons: parseInt(plan.num_lessons),
        lessons: lessons,
    };
}

function lessonInputsKey(planObj) {
    return JSON.stringify({
        subject: planObj.subject,
        goal: planObj.goal,
        previous_knowledge: planObj.previous_knowledge,
        needs: planObj.needs,
    });
}

const AddStudyPlan = ({ username, childProfile, onOk }) => {
    const [plan, setPlan] = useState({
        subject: '',
        goal: '',
        previous_knowledge: '',
        needs: '',
        num_lessons: '5',
        emoji: '',
        lessons: [],
    });
    const [lessons, setLessons] = useState([]);
    const [originalGeneratedLessons, setOriginalGeneratedLessons] = useState([]);
    const [lessonsWithTopics, setLessonsWithTopics] = useState([]);
    const [originalGeneratedLessonsWithTopics, setOriginalGeneratedLessonsWithTopics] = useState([]);
    const [studyPlanDraftId, setStudyPlanDraftId] = useState('');
    const [isTopicsInitialLoading, setIsTopicsInitialLoading] = useState(false);
    const [lessonsGeneratedKey, setLessonsGeneratedKey] = useState(null);

    const [planProp, setPlanProp] = useState({ nonse: 0, num_lessons: plan.num_lessons, num_topics: 3 });

    const subjects = [
        { name: 'Math', emoji: '🔢' },
        { name: 'Science', emoji: '🔬' },
        { name: 'English', emoji: '📖' },
        { name: 'History', emoji: '🏛️' },
        { name: 'Social studies', emoji: '🤝' },
        { name: 'Geography', emoji: '🗺️' },
        { name: 'Art', emoji: '🎨' },
        { name: 'Environmental studies', emoji: '🌱' },
        { name: 'Physics', emoji: '⚛️' },
        { name: 'Chemistry', emoji: '🧪' },
        { name: 'Robotics', emoji: '🤖' },
        { name: 'Coding', emoji: '💻' },
        { name: 'Chess', emoji: '♟️' },
        { name: 'Economics', emoji: '📈' },
        { name: 'Financial literacy', emoji: '💵' },
        { name: 'Personal development', emoji: '⭐' },
        { name: 'Creative writing', emoji: '✍️' },
        { name: 'Photography', emoji: '📷' },
    ];

    const [goals, setGoals] = useState([]);
    const [isGoalsModified, setIsGoalsModified] = useState(false);

    const [previousKnowledges, setPreviousKnowledges] = useState([]);
    const [, setIsPreviousKnowledgeModified] = useState(false);

    const [needs] = useState([]);
    const [, setIsNeedsModified] = useState(false);

    const [emojis, setEmojis] = useState([]);

    const fetchGoals = async () => {
        setGoals([]);
        setIsGoalsModified(false);
        await streamGoals(plan, username, setGoals);
    };

    const fetchPreviousKnowledge = async () => {
        setPreviousKnowledges([]);
        setIsPreviousKnowledgeModified(false);
        await streamPreviousKnowledge(plan, username, setPreviousKnowledges);
    };

    const navigate = useNavigate();

    const navigateToRoot = () => {
        navigate('');
    };

    const navigateToGoal = async () => {
        navigate('goal');
        await fetchGoals();
    };

    const navigateToPreviousKnowledge = async () => {
        navigate('previous_knowledge');
        await fetchPreviousKnowledge();
    };

    const navigateToEmojis = async () => {
        navigate('emojis');
        setEmojis([]);
        await getEmojis(plan, username, setEmojis);
    };

    const fetchSuggestedStudyPlan = async () => {
        setLessons([]);
        setOriginalGeneratedLessons([]);
        setLessonsWithTopics([]);
        setOriginalGeneratedLessonsWithTopics([]);
        setStudyPlanDraftId('');
        const generatedLessons = await streamStudyPlan(plan, username, planProp, 'gemini', setLessons);
        setLessons(generatedLessons);
        setOriginalGeneratedLessons(generatedLessons);
        setLessonsGeneratedKey(lessonInputsKey(plan));
    };

    const fetchSuggestedTopics = async (inputLessons) => {
        const generatedLessonsWithTopics = await streamStudyPlanTopics(
            plan,
            username,
            inputLessons,
            planProp,
            'gemini',
            setLessonsWithTopics,
        );
        setLessonsWithTopics(generatedLessonsWithTopics);
        setOriginalGeneratedLessonsWithTopics(generatedLessonsWithTopics);
    };

    const navigateToPreview = async () => {
        navigate('preview');
        const currentKey = lessonInputsKey(plan);
        if (lessons.length === 0 || lessonsGeneratedKey !== currentKey) {
            await fetchSuggestedStudyPlan();
        }
    };

    const navigateToTopics = async () => {
        setPlan({ ...plan, num_lessons: planProp.num_lessons, lessons });
        navigate('topics');
        const shouldShowInitialTopicsLoading = lessonsWithTopics.length === 0;
        if (shouldShowInitialTopicsLoading) {
            setIsTopicsInitialLoading(true);
        }
        try {
            await fetchSuggestedTopics(lessons);
        }
        finally {
            if (shouldShowInitialTopicsLoading) {
                setIsTopicsInitialLoading(false);
            }
        }
    };

    const renderStep = (stepNum) => {
        return <div>
            <StepIndicator
                currentStep={stepNum} numSteps={6}
                onClicks={[
                    navigateToRoot, navigateToGoal, navigateToPreviousKnowledge,
                    navigateToEmojis, navigateToPreview,
                ]} />
        </div>;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setPlan((prevState) => ({ ...prevState, [name]: value }));

        if (name === 'goal') {
            setIsGoalsModified(true);
        } else if (name === 'previous_knowledge') {
            setIsPreviousKnowledgeModified(true);
        } else if (name === 'needs') {
            setIsNeedsModified(true);
        }
    };

    const childName = childProfile?.first_name || 'your child';

    return <div className="text-left mx-auto max-w-screen-lg">
        <h1 className="text-2xl">What should {childName} learn next?</h1>

        <div className="mb-8">
            <div>
                Create a personalized study plan to guide {childName} through their learning goals.
            </div>
        </div>

        <Routes>
            <Route path="" element={<div>
                {renderStep(1)}
                <div className="text-lg">
                    Enter the subject:
                </div>
                <input
                    type="text"
                    className="mt-2 w-full md:w-2/3 border border-gray-300 p-1"
                    name="subject"
                    value={plan.subject}
                    maxLength={30}
                    onChange={handleChange} />

                <div className="my-6 md:w-2/3">
                    {
                        subjects.map((subject, index) => (
                            <button key={index}
                                className={`${plan.subject === subject.name ? 'text-blue-500 border-blue-500' : 'text-gray-500 border-gray-500'} bg-white text-sm border rounded-lg p-1 mr-1 mb-2`}
                                onClick={() => setPlan({ ...plan, subject: subject.name, emoji: subject.emoji })}>
                                <span className="mr-1">{subject.emoji}</span>{subject.name}
                            </button>
                        ))
                    }
                </div>

                <button
                    className={`${plan.subject.length === 0 ? 'bg-gray-400' : 'bg-blue-500'} text-white py-2 px-4 rounded`}
                    disabled={plan.subject.length === 0}
                    onClick={navigateToGoal}>
                    Next
                </button>
            </div>} />
            <Route path="goal" element={<div>
                {renderStep(2)}
                <div className="text-lg">
                    Enter learning goals:
                </div>
                <textarea
                    className="mt-2 w-full border border-gray-300 p-1 h-[5rem] sm:h-[4rem]"
                    name="goal"
                    value={plan.goal}
                    maxLength={120}
                    onChange={handleChange} />

                <div className="my-2">
                    {
                        goals.length === 0 && <LoadingIndicator />
                    }
                    {isGoalsModified &&
                        <div className="my-2">
                            <LoadingButton
                                imgSrc="/icon-refresh.png"
                                text="Generate new suggestions"
                                onClick={async () => await fetchGoals()} />
                        </div>
                    }
                    {
                        goals.map((goal, index) => (
                            <button key={index}
                                className={`${plan.goal === goal ? 'text-blue-500 border-blue-500' : 'text-gray-500 border-gray-500'} bg-white text-sm text-left border rounded-lg p-1 mr-1 mb-2`}
                                onClick={() => setPlan({ ...plan, goal: goal })}>
                                {goal}
                            </button>
                        ))
                    }
                </div>

                <button
                    className={`${plan.goal.length === 0 ? 'bg-gray-400' : 'bg-blue-500'} text-white py-2 px-4 rounded`}
                    disabled={plan.goal.length === 0}
                    onClick={navigateToPreviousKnowledge}>
                    Next
                </button>
            </div>} />
            <Route path="previous_knowledge" element={<div>
                {renderStep(3)}
                <div className="text-lg">
                    Enter previous knowledge:
                </div>
                <textarea
                    className="mt-2 w-full border border-gray-300 p-1 h-[5rem] sm:h-[4rem]"
                    name="previous_knowledge"
                    value={plan.previous_knowledge}
                    maxLength={120}
                    onChange={handleChange} />

                <div className="my-6">
                    {
                        previousKnowledges.length === 0 && <LoadingIndicator />
                    }
                    {
                        previousKnowledges.map((previousKnowledge, index) => (
                            <button key={index}
                                className={`${plan.previous_knowledge === previousKnowledge ? 'text-blue-500 border-blue-500' : 'text-gray-500 border-gray-500'} bg-white text-sm text-left border rounded-lg p-1 mr-1 mb-2`}
                                onClick={() => setPlan({ ...plan, previous_knowledge: previousKnowledge })}>
                                {previousKnowledge}
                            </button>
                        ))
                    }
                </div>

                <button
                    className={`${plan.previous_knowledge.length === 0 ? 'bg-gray-400' : 'bg-blue-500'} text-white py-2 px-4 rounded`}
                    disabled={plan.previous_knowledge.length === 0}
                    onClick={navigateToEmojis}>
                    Next
                </button>
            </div>} />
            <Route path="needs" element={<div>
                {renderStep(4)}
                <div className="text-lg">
                    Enter educational needs (optional):
                </div>
                <textarea
                    className="mt-2 w-full border border-gray-300 p-1 h-[5rem] sm:h-[4rem]"
                    name="needs"
                    placeholder="eg, Requires extra help with grammar and sentence structure, etc"
                    value={plan.needs}
                    maxLength={120}
                    onChange={handleChange} />

                <div className="my-6">
                    {
                        needs.length === 0 && <LoadingIndicator />
                    }
                    {
                        needs.map((needs, index) => (
                            <button key={index}
                                className={`${plan.needs === needs ? 'text-blue-500 border-blue-500' : 'text-gray-500 border-gray-500'} bg-white text-sm text-left border rounded-lg p-1 mr-1 mb-2`}
                                onClick={() => setPlan({ ...plan, needs: needs })}>
                                {needs}
                            </button>
                        ))
                    }
                </div>

                <button className="bg-blue-500 text-white py-2 px-4 rounded"
                    onClick={navigateToEmojis}>
                    Next
                </button>
            </div>} />
            <Route path="emojis" element={<div>
                {renderStep(4)}
                <div className="text-lg">
                    Enter study plan emoji:
                </div>
                <input
                    type="text"
                    className="mt-2 w-30 border border-gray-300 p-1"
                    name="emoji"
                    maxLength={2}
                    value={plan.emoji}
                    onChange={handleChange} />

                <div className="my-6">
                    {
                        emojis.length === 0 && <LoadingIndicator />
                    }
                    {
                        emojis.map((emoji, index) => (
                            <button key={index}
                                className={`${plan.emoji === emoji ? 'text-blue-500 border-blue-500' : 'text-gray-500 border-gray-500'} bg-white text-sm border rounded-lg p-1 mr-2 mb-2`}
                                onClick={() => setPlan({ ...plan, emoji: emoji })}>
                                {emoji}
                            </button>
                        ))
                    }
                </div>

                <button
                    className={`${plan.emoji.length === 0 ? 'bg-gray-400' : 'bg-blue-500'} text-white py-2 px-4 rounded`}
                    disabled={plan.emoji.length === 0}
                    onClick={navigateToPreview}>
                    Preview study plan
                </button>
            </div>} />
            <Route path="preview" element={<div>
                {renderStep(5)}
                <div className="text-lg">
                    Review study plan
                </div>
                <div>
                    Here is your suggested study plan. You can edit, reorder, or regenerate parts before continuing.
                </div>

                <ReviewLessons
                    lessons={lessons}
                    originalGeneratedLessons={originalGeneratedLessons}
                    plan={plan}
                    username={username}
                    planProp={planProp}
                    setPlanProp={setPlanProp}
                    studyPlanDraftId={studyPlanDraftId}
                    setStudyPlanDraftId={setStudyPlanDraftId}
                    setLessons={setLessons}
                />

                <div className="mt-4">
                    <button
                        className={`${lessons.length === 0 ? 'bg-gray-400' : 'bg-blue-500'} text-white py-2 px-4 rounded`}
                        disabled={lessons.length === 0}
                        onClick={navigateToTopics}>
                        Continue to topics
                    </button>
                </div>
            </div>} />

            <Route path="topics" element={<div>
                {renderStep(6)}
                <div className="text-lg">
                    Review topics
                </div>
                <div>
                    Here is your suggested study plan with topics. You can edit, reorder, or regenerate parts before accepting.
                </div>

                {isTopicsInitialLoading ? (
                    <div className="my-4">
                        <FunLoader
                            title="Building your study plan..."
                            subtitle="Generating topics for each lesson"
                            ariaLabel="Generating study plan topics"
                        />
                    </div>
                ) : (
                    <ReviewTopics
                        lessonsWithTopics={lessonsWithTopics}
                        originalGeneratedLessonsWithTopics={originalGeneratedLessonsWithTopics}
                        plan={plan}
                        username={username}
                        lessons={lessons}
                        planProp={planProp}
                        setPlanProp={setPlanProp}
                        studyPlanDraftId={studyPlanDraftId}
                        setStudyPlanDraftId={setStudyPlanDraftId}
                        setLessonsWithTopics={setLessonsWithTopics}
                    />
                )}

                <div className="mt-4">
                    <LoadingButton
                        text="Accept study plan"
                        disabled={lessonsWithTopics.length === 0}
                        onClick={async () => onOk(getStudyPlan(plan, lessonsWithTopics))} />
                </div>
            </div>} />
        </Routes>
    </div>;
};

export default AddStudyPlan;
